import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router";
import { Box, Spinner } from "@chakra-ui/react";
import Layout from "./Layout/Index";
import AuthLayout from "./Layout/Auth";

// Eager load critical routes
import LoginPage from "./pages/Login";
import AdminLogin from "./pages/AdminLogin";
import ForgotPasswordPage from "./pages/ForgotPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Lazy load all other routes
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Notifications = lazy(() => import("./pages/Notifications"));
const UserManagement = lazy(() => import("./pages/User"));
const CreateUser = lazy(() => import("./pages/User/UserWithStorage"));
const CreateOnlyCustomer = lazy(() => import("./pages/User/createCustomer"));
const UserDetails = lazy(() => import("./pages/User/UserDetails"));
const StoreUnitManagement = lazy(() => import("./pages/StorageUnit"));
const ProfilePage = lazy(() => import("./pages/Profile"));
const Inventory = lazy(() => import("./pages/Inventory"));
const BulkPrint = lazy(() => import("./pages/BulkPrint"));
const CreateStock = lazy(() => import("./pages/Inventory/NewStock"));
const Dispatch = lazy(() => import("./pages/Inventory/dispatch"));
// UpdateStock removed — unified into NewStock (CreateStock) form
const StockHistory = lazy(() => import("./pages/Inventory/StockHistory"));
const AssignCrates = lazy(() => import("./pages/Inventory/AssignCrates"));
const UpdateUser = lazy(() => import("./pages/User/updateUser"));
const CreateStorageUint = lazy(() => import("./pages/StorageUnit/createStore"));
const StorageUnitDetails = lazy(() => import("./pages/StorageUnit/StorageUnitDetails"));
const PartnerDeviceMapping = lazy(() => import("./pages/StorageUnit/PartnerDeviceMapping"));
const ChamberManagement = lazy(() => import("./pages/StorageUnit/chamber"));
const AddChamberForm = lazy(() => import("./pages/StorageUnit/AddChamber"));
const ChamberTelemetry = lazy(() => import("./pages/StorageUnit/ChamberTelemetry"));
const Application = lazy(() => import("./pages/User/application"));
const ApplicationUser = lazy(() => import("./pages/User/applicationuser"));
const Invoice = lazy(() => import("./pages/invoices"));
const ExternalUser = lazy(() => import("./pages/ExternalUser"));
const CreateExternalUser = lazy(() => import("./pages/ExternalUser/createExternalUser"));
const Booking = lazy(() => import("./pages/Booking"));

// Vehicle Module
const VehicleList = lazy(() => import("./pages/Vehicles"));
const VehicleDetail = lazy(() => import("./pages/Vehicles/VehicleDetail"));

// Admin Vehicle Module
const AdminVehicleList = lazy(() => import("./pages/Admin/Vehicles"));
const AdminVehicleDetail = lazy(() => import("./pages/Admin/Vehicles/VehicleDetail"));

const UserList = lazy(() => import("./pages/Partner/Users/UserList"));
const CreatePartnerUser = lazy(() => import("./pages/Partner/Users/CreateUser"));
const UserProfile = lazy(() => import("./pages/Partner/Users/UserProfile"));

// Admin Module
const PartnerList = lazy(() => import("./pages/Admin/Partners/PartnerList"));
const AddPartner = lazy(() => import("./pages/Admin/Partners/AddPartner"));
const PartnerDetails = lazy(() => import("./pages/Admin/Partners/PartnerDetails"));
const EditPartner = lazy(() => import("./pages/Admin/Partners/EditPartner"));

// Cold Storage Module
const ColdStorageList = lazy(() => import("./pages/Admin/ColdStorage/ColdStorageList"));
const CreateColdStorage = lazy(() => import("./pages/Admin/ColdStorage/CreateColdStorage"));
const ColdStorageDetails = lazy(() => import("./pages/Admin/ColdStorage/ColdStorageDetails"));
const EditColdStorage = lazy(() => import("./pages/Admin/ColdStorage/EditColdStorage"));
const ChamberList = lazy(() => import("./pages/Admin/ColdStorage/Chambers/ChamberList"));
const ConfigureChamber = lazy(() => import("./pages/Admin/ColdStorage/Chambers/ConfigureChamber"));
const FloorList = lazy(() => import("./pages/Admin/ColdStorage/Floors/FloorList"));
const DeviceMapping = lazy(() => import("./pages/Admin/ColdStorage/Devices/DeviceMapping"));

// Super Admin Module
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdmin/Dashboard"));
const SuperAdminUsers = lazy(() => import("./pages/SuperAdmin/UsersOverview"));

// Loading fallback component
const PageLoader = () => (
  <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
    <Spinner size="xl" color="blue.500" />
  </Box>
);

const AppRouters = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes under Layout */}
          <Route element={<Layout />}>
            <Route path="/" element={<LoginPage />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Route>

          {/* Protected/App Routes under AuthLayout */}
          <Route element={<AuthLayout />}>
            <Route path="/app/:index" element={<Dashboard />} />
            <Route path="/app/:index/alerts" element={<Alerts />} />
            <Route path="/app/:index/notifications" element={<Notifications />} />
            <Route path="/app/:index/users" element={<UserManagement />} />
            <Route path="/app/:index/users/new" element={<CreateUser />} />
            <Route path="/app/:index/users/onlyuser" element={<UpdateUser />} />
            <Route path="/app/:index/users/details/:id" element={<UserDetails />} />
            <Route path="/app/:index/customers/onlycustomers" element={<CreateOnlyCustomer />} />
            <Route path="/app/:index/storageunit" element={<StoreUnitManagement />} />
            <Route path="/app/:index/storageunit/new" element={<CreateStorageUint />} />
            <Route path="/app/:index/storageunit/details/:id" element={<StorageUnitDetails />} />
            <Route path="/app/:index/storageunit/chamber/:id" element={<ChamberManagement />} />
            <Route path="/app/:index/storageunit/chamber/new/:id" element={<AddChamberForm />} />
            <Route
              path="/app/:index/storageunit/:storageId/chamber/:id/telemetry"
              element={<ChamberTelemetry />}
            />
            <Route path="/app/:index/inventory" element={<Inventory />} />
            <Route path="/app/:index/bulk-print" element={<BulkPrint />} />
            <Route path="/app/:index/inventory/new" element={<CreateStock />} />
            <Route path="/app/:index/inventory/dispatch" element={<Dispatch />} />

            <Route path="/app/:index/inventory/stock/:id" element={<StockHistory />} />
            <Route path="/app/:index/inventory/stock/:id/assign" element={<AssignCrates />} />
            <Route path="/app/:index/pre-inspection" element={<ApplicationUser />} />
            <Route path="/app/:index/pre-inspection/add" element={<Application />} />
            <Route path="/app/:index/invoices" element={<Invoice />} />
            <Route path="/app/:index/external-user" element={<ExternalUser />} />
            <Route path="/app/:index/external-user/new" element={<CreateExternalUser />} />
            <Route path="/app/:index/profile" element={<ProfilePage />} />
            <Route path="/app/:index/bookings" element={<Booking />} />

            <Route path="/app/partner/users" element={<UserList />} />
            <Route path="/app/partner/users/create" element={<CreatePartnerUser />} />
            <Route path="/app/partner/users/profile/:id" element={<UserProfile />} />

            {/* Partner Flow Routes (non-admin users) */}
            <Route path="/app/partner/dashboard" element={<Dashboard />} />
            <Route path="/app/partner/alerts" element={<Alerts />} />
            <Route path="/app/partner/notifications" element={<Notifications />} />
            <Route path="/app/partner/customers" element={<UserManagement />} />
            <Route path="/app/partner/customers/new" element={<CreateOnlyCustomer />} />
            <Route path="/app/partner/customers/details/:id" element={<UserDetails />} />
            <Route path="/app/partner/storageunit" element={<StoreUnitManagement />} />
            <Route path="/app/partner/storageunit/new" element={<CreateStorageUint />} />
            <Route path="/app/partner/storageunit/details/:id" element={<StorageUnitDetails />} />
            <Route path="/app/partner/storageunit/chamber/:id" element={<ChamberManagement />} />
            <Route path="/app/partner/storageunit/chamber/new/:id" element={<AddChamberForm />} />
            <Route
              path="/app/partner/storageunit/:storageId/chamber/:id/telemetry"
              element={<ChamberTelemetry />}
            />
            <Route
              path="/app/partner/storageunit/:storageId/devices"
              element={<PartnerDeviceMapping />}
            />
            <Route path="/app/partner/inventory" element={<Inventory />} />
            <Route path="/app/partner/bulk-print" element={<BulkPrint />} />
            <Route path="/app/partner/inventory/new" element={<CreateStock />} />
            <Route path="/app/partner/inventory/dispatch" element={<Dispatch />} />

            <Route path="/app/partner/inventory/stock/:id" element={<StockHistory />} />
            <Route path="/app/partner/inventory/stock/:id/assign" element={<AssignCrates />} />
            <Route path="/app/partner/bookings" element={<Booking />} />
            <Route path="/app/partner/vehicles" element={<VehicleList />} />
            <Route path="/app/partner/vehicles/:vehicleId" element={<VehicleDetail />} />
            <Route path="/app/partner/profile" element={<ProfilePage />} />
            <Route path="/app/partner/invoices" element={<Invoice />} />

            {/* Admin Module Routes */}
            <Route path="/app/admin/partners" element={<PartnerList />} />
            <Route path="/app/admin/partners/add" element={<AddPartner />} />
            <Route path="/app/admin/partners/edit/:id" element={<EditPartner />} />
            <Route path="/app/admin/partners/:id" element={<PartnerDetails />} />

            {/* Admin Vehicle Routes */}
            <Route path="/app/admin/vehicles" element={<AdminVehicleList />} />
            <Route path="/app/admin/vehicles/:vehicleId" element={<AdminVehicleDetail />} />

            {/* Cold Storage Module Routes */}
            <Route path="/app/admin/cold-storage" element={<ColdStorageList />} />
            <Route path="/app/admin/cold-storage/create" element={<CreateColdStorage />} />
            <Route path="/app/admin/cold-storage/:id" element={<ColdStorageDetails />} />
            <Route path="/app/admin/cold-storage/edit/:id" element={<EditColdStorage />} />
            <Route path="/app/admin/cold-storage/:id/chambers" element={<ChamberList />} />
            <Route
              path="/app/admin/cold-storage/:id/chambers/configure/:chamberId"
              element={<ConfigureChamber />}
            />
            <Route path="/app/admin/cold-storage/:id/floors" element={<FloorList />} />
            <Route path="/app/admin/cold-storage/:id/devices" element={<DeviceMapping />} />

            {/* Super Admin Module Routes */}
            <Route path="/app/superadmin/dashboard" element={<SuperAdminDashboard />} />
            <Route path="/app/superadmin/admins" element={<SuperAdminUsers />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRouters;
